import React, { useState } from 'react';
import type { BodyIssueCheckInRecord, BodyIssueRecord, SymptomType } from '../../types/bodyMapIssue';
import { createBodyIssueCheckIn } from '../../services/bodyMapIssueService';

const SYMPTOM_OPTIONS: Array<{ value: SymptomType; label: string }> = [
  { value: 'sharp_pain', label: 'Sharp pain' },
  { value: 'dull_pain', label: 'Dull pain' },
  { value: 'stiffness', label: 'Stiffness' },
  { value: 'swelling', label: 'Swelling' },
  { value: 'numbness', label: 'Numbness' },
  { value: 'tingling', label: 'Tingling' },
  { value: 'weakness', label: 'Weakness' },
  { value: 'fatigue', label: 'Fatigue' },
  { value: 'other', label: 'Other' },
];

interface Props {
  userId: string;
  issue: BodyIssueRecord;
  onSaved: () => Promise<void> | void;
}

export default function IssueCheckInForm({ userId, issue, onSaved }: Props) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    betterSameWorse: 'normal' as BodyIssueCheckInRecord['betterSameWorse'],
    painScore: '',
    symptomTypes: [] as SymptomType[],
    visibleChange: 'none' as BodyIssueCheckInRecord['visibleChange'],
    functionImpact: 'none' as BodyIssueCheckInRecord['functionImpact'],
    freeText: '',
  });

  const toggleSymptom = (value: SymptomType) => {
    setForm((current) => ({
      ...current,
      symptomTypes: current.symptomTypes.includes(value)
        ? current.symptomTypes.filter((item) => item !== value)
        : [...current.symptomTypes, value],
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const painScore = form.painScore ? Number(form.painScore) : null;
      const redFlagTriggered = (painScore != null && painScore >= 8)
        || form.visibleChange === 'visible_damage'
        || form.functionImpact === 'severe';
      const redFlagReason = redFlagTriggered
        ? 'High pain, severe functional impact, or visible damage selected.'
        : null;

      await createBodyIssueCheckIn(userId, issue.id || '', {
        regionKey: issue.regionKey,
        betterSameWorse: form.betterSameWorse,
        painScore,
        symptomTypes: form.symptomTypes,
        visibleChange: form.visibleChange,
        functionImpact: form.functionImpact,
        freeText: form.freeText,
        redFlagTriggered,
        redFlagReason,
        suggestedFollowUp: redFlagTriggered
          ? 'review_needed'
          : form.betterSameWorse === 'worse_than_normal'
            ? 'next_day'
            : 'three_days',
      });

      setForm({
        betterSameWorse: 'normal',
        painScore: '',
        symptomTypes: [],
        visibleChange: 'none',
        functionImpact: 'none',
        freeText: '',
      });
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
      <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
        Daily or episodic check-in
      </p>
      <h2 className="text-2xl font-normal mb-5" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
        How is it feeling today?
      </h2>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Compared with normal</label>
          <div className="flex flex-wrap gap-2">
            {[
              ['better_than_normal', 'Better than normal'],
              ['normal', 'Normal'],
              ['worse_than_normal', 'Worse than normal'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setForm((current) => ({ ...current, betterSameWorse: value as BodyIssueCheckInRecord['betterSameWorse'] }))}
                className="px-3 py-2 rounded-full text-sm"
                style={{
                  backgroundColor: form.betterSameWorse === value ? 'var(--melio-charcoal)' : '#EFEEE9',
                  color: form.betterSameWorse === value ? 'var(--melio-cream)' : 'var(--melio-charcoal)',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Pain score (optional)</label>
          <input
            type="number"
            min={0}
            max={10}
            value={form.painScore}
            onChange={(event) => setForm((current) => ({ ...current, painScore: event.target.value }))}
            className="w-full rounded-xl border px-4 py-3 text-sm"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Symptoms</label>
          <div className="flex flex-wrap gap-2">
            {SYMPTOM_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => toggleSymptom(option.value)}
                className="px-3 py-2 rounded-full text-sm"
                style={{
                  backgroundColor: form.symptomTypes.includes(option.value) ? 'rgba(79, 93, 24, 0.18)' : '#EFEEE9',
                  color: 'var(--melio-charcoal)',
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Visible change</span>
            <select
              value={form.visibleChange}
              onChange={(event) => setForm((current) => ({ ...current, visibleChange: event.target.value as BodyIssueCheckInRecord['visibleChange'] }))}
              className="w-full rounded-xl border px-4 py-3 text-sm"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
            >
              <option value="none">None</option>
              <option value="swelling">Swelling</option>
              <option value="bruising">Bruising</option>
              <option value="redness">Redness</option>
              <option value="visible_damage">Visible damage</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Function impact</span>
            <select
              value={form.functionImpact}
              onChange={(event) => setForm((current) => ({ ...current, functionImpact: event.target.value as BodyIssueCheckInRecord['functionImpact'] }))}
              className="w-full rounded-xl border px-4 py-3 text-sm"
              style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
            >
              <option value="none">None</option>
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--melio-charcoal)' }}>Notes</label>
          <textarea
            rows={4}
            value={form.freeText}
            onChange={(event) => setForm((current) => ({ ...current, freeText: event.target.value }))}
            className="w-full rounded-xl border px-4 py-3 text-sm"
            style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
            placeholder="What happened today? Any trigger, load increase, or relevant detail?"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
        >
          {saving ? 'Saving...' : 'Save check-in'}
        </button>
      </form>
    </section>
  );
}
